import { useQuery } from '@apollo/client';
import Button from '@mui/material/Button';
import gql from 'graphql-tag';
import { truncate } from 'lodash';
import { findIndex } from 'lodash';
import uniqueId from 'lodash/uniqueId';
import { forwardRef } from 'react';
import { useImperativeHandle } from 'react';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import React from 'react';
import ItemTable from '../../components/ItemTable';
import useMutationFHG from '../../fhg/components/data/useMutationFHG';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import FHGTypography from '../../fhg/components/Typography';
import FaqEdit from './FaqEdit';

/**
 * Flatten Markdown to rough plain text for the low-fidelity list-column preview
 * (the full render lives in the edit dialog and the mobile app).
 */
function stripMarkdown(text) {
   return (text || '')
      .replace(/^#{1,6}\s+/gm, '')          // headings
      .replace(/^\s*>\s?/gm, '')            // block quotes
      .replace(/^\s*[-*+]\s+/gm, '')        // bullet list markers
      .replace(/^\s*\d+\.\s+/gm, '')        // ordered list markers
      .replace(/!\[[^\]]*\]\([^)]*\)/g, '') // images
      .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // links -> text
      .replace(/(\*\*|__)(.*?)\1/g, '$2')   // bold
      .replace(/(\*|_)(.*?)\1/g, '$2')      // italic
      .replace(/`([^`]*)`/g, '$1')          // inline code
      .replace(/\s+/g, ' ')
      .trim();
}

const FAQ_INFO_FRAGMENT = gql`
   fragment faqInfo on Faq {
      id
      question
      answer
      sortOrder
      operatorId
   }
`;

const FAQ_GENERAL_QUERY = gql`
   query getGeneralFaq {
      faqs: faq_General {
         ...faqInfo
      }
   }
   ${FAQ_INFO_FRAGMENT}
`;

const FAQ_BY_OPERATOR_QUERY = gql`
   query getFaqByOperator($operatorId: Int!) {
      faqs: faq_ByOperatorId(operatorId: $operatorId) {
         ...faqInfo
      }
   }
   ${FAQ_INFO_FRAGMENT}
`;

const FAQ_CREATE = gql`
   mutation faqCreate($operatorId: Int, $question: String!, $answer: String!, $sortOrder: Int) {
      faq: faq_Create(faq: {operatorId: $operatorId, question: $question, answer: $answer, sortOrder: $sortOrder}) {
         ...faqInfo
      }
   }
   ${FAQ_INFO_FRAGMENT}
`;

const FAQ_UPDATE = gql`
   mutation faqUpdate($id: Int!, $question: String!, $answer: String!, $sortOrder: Int!) {
      faq: faq_Update(faqId: $id, faq: {question: $question, answer: $answer, sortOrder: $sortOrder}) {
         ...faqInfo
      }
   }
   ${FAQ_INFO_FRAGMENT}
`;

const FAQ_DELETE = gql`
   mutation faqDelete($id: Int!) {
      faq_Delete(faqId: $id)
   }
`;

/**
 * Add/edit/delete table for FAQ entries. When operatorId is null/undefined the table manages the general FAQ set
 * (operatorId IS NULL); otherwise it manages that operator's set. Mutations run immediately and the list refetches -
 * the parent only needs the exposed refetch() (used when switching to this tab on the operator page).
 */
const FaqTable = forwardRef(function FaqTable({ operatorId }, ref) {
   const isGeneral = operatorId === null || operatorId === undefined;

   const query = isGeneral ? FAQ_GENERAL_QUERY : FAQ_BY_OPERATOR_QUERY;
   const variables = isGeneral ? undefined : { operatorId };

   const { data, loading, error, refetch } = useQuery(query, { variables, skip: !isGeneral && !operatorId });

   const refetchQueries = useCallback(() => ([{ query, variables }]), [query, variables]);

   const [faqCreate] = useMutationFHG(FAQ_CREATE, { refetchQueries });
   const [faqUpdate] = useMutationFHG(FAQ_UPDATE, { refetchQueries });
   const [faqDelete] = useMutationFHG(FAQ_DELETE, { refetchQueries });

   const [faqId, setFaqId] = useState();
   const [faqValue, setFaqValue] = useState();
   const [showEdit, setShowEdit] = useState(false);
   const [pendingDelete, setPendingDelete] = useState();
   const [isSaving, setIsSaving] = useState(false);

   useImperativeHandle(ref, () => ({
      refetch: () => refetch(),
   }), [refetch]);

   const faqs = useMemo(() => {
      const rows = [...(data?.faqs || [])];
      return rows.sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));
   }, [data]);

   const handleSelect = useCallback((faq) => {
      setFaqValue(faq);
      setFaqId(faq?.id);
   }, []);

   const handleAdd = useCallback(() => {
      const nextOrder = faqs.length ? Math.max(...faqs.map((faq) => faq.sortOrder || 0)) + 1 : 0;
      setFaqValue({ id: uniqueId('new-'), question: '', answer: '', sortOrder: nextOrder });
      setShowEdit(true);
   }, [faqs]);

   const handleEdit = useCallback(() => {
      setShowEdit(true);
   }, []);

   const handleEditCompleted = useCallback(async (updatedValue) => {
      setShowEdit(false);
      setIsSaving(true);
      try {
         const isNew = findIndex(faqs, { id: updatedValue.id }) < 0;
         if (isNew) {
            await faqCreate({
               variables: {
                  operatorId: isGeneral ? null : operatorId,
                  question: updatedValue.question,
                  answer: updatedValue.answer,
                  sortOrder: parseInt(updatedValue.sortOrder, 10) || 0,
               },
            });
         } else {
            await faqUpdate({
               variables: {
                  id: updatedValue.id,
                  question: updatedValue.question,
                  answer: updatedValue.answer,
                  sortOrder: parseInt(updatedValue.sortOrder, 10) || 0,
               },
            });
         }
         setFaqId(undefined);
         setFaqValue(undefined);
      } finally {
         setIsSaving(false);
      }
   }, [faqs, faqCreate, faqUpdate, isGeneral, operatorId]);

   const handleDeleteConfirm = useCallback(async () => {
      setIsSaving(true);
      try {
         await faqDelete({ variables: { id: pendingDelete.id } });
         setPendingDelete(undefined);
         setFaqId(undefined);
         setFaqValue(undefined);
      } finally {
         setIsSaving(false);
      }
   }, [faqDelete, pendingDelete]);

   const columns = useMemo(() => ([
      { Header: <FHGTypography id={'faq.order.column'} />, accessor: 'sortOrder' },
      {
         Header: <FHGTypography id={'faq.question.column'} />,
         accessor: 'question',
         Cell: ({ value }) => truncate(value, { length: 90 }),
      }, {
         Header: <FHGTypography id={'faq.answer.column'} />,
         accessor: 'answer',
         Cell: ({ value }) => truncate(stripMarkdown(value), { length: 120 }),
      },
   ]), []);

   if (error) {
      return <DataLoadingAndErrors error={error} isLoading={false} />;
   }

   return (
      <>
         <DataLoadingAndErrors error={error} isLoading={loading || isSaving} />
         <ItemTable titleKey={'faq.title'} columns={columns} items={faqs}
            onSelect={handleSelect} selectId={faqId} showSearchTextField={false}>
            <Button color='primary' onClick={handleAdd} style={{ marginLeft: 8 }}>
               <FHGTypography noWrap id={'faq.add.button'} />
            </Button>
            <Button color='primary' onClick={handleEdit} style={{ marginLeft: 8 }} disabled={!faqId}>
               <FHGTypography noWrap id={'faq.edit.button'} disabled={!faqId} />
            </Button>
            <Button color='primary' onClick={() => setPendingDelete(faqValue)} style={{ marginLeft: 8 }} disabled={!faqId}>
               <FHGTypography noWrap id={'faq.delete.button'} disabled={!faqId} />
            </Button>
         </ItemTable>
         {showEdit && (
            <FaqEdit
               faqValue={faqValue}
               onSubmit={handleEditCompleted}
               onClose={() => setShowEdit(false)}
            />
         )}
         {pendingDelete && (
            <ModalDialog
               open={true}
               titleKey={'faq.delete.title'}
               messageKey={'faq.delete.message'}
               submitKey={'delete.button'}
               submitColor={'error'}
               onSubmit={handleDeleteConfirm}
               onClose={() => setPendingDelete(undefined)}
               isSaving={isSaving}
               isEnabled={!isSaving}
               maxWidth={'sm'}
               fullWidth
            />
         )}
      </>
   );
});

export default FaqTable;
